import { ArrowLeft, Flame, Heart, Sparkles, PenTool, Star, Scroll } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function CommitmentRitualGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-purple-700 hover:text-purple-800 hover:bg-purple-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-purple-100 rounded-full">
              <Flame className="w-8 h-8 text-purple-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Commitment Ritual</h1>
              <p className="text-purple-600">Week 12, Day 7 - Keeping Your Glow</p>
            </div>
          </div>

          <div className="prose prose-purple max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Create a meaningful ritual to close this chapter and open the next. Light a candle, 
              put on special music, and read aloud a commitment letter to yourself. This is a 
              promise to the person you've become.
            </p>

            <div className="bg-purple-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-purple-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Why Ritual Matters
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Creates meaning and marks important transitions</li>
                <li>• Deepens commitment through intentional action</li>
                <li>• Honors your growth and transformation</li>
                <li>• Bridges from program to lifestyle</li>
                <li>• Provides a moment to fully acknowledge yourself</li>
                <li>• Creates a memory and anchor point you can return to</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Setting the Scene</h2>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white border-2 border-purple-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-2">Physical Space</h3>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Find a quiet, private space</li>
                  <li>• Light a candle or use soft lighting</li>
                  <li>• Sit somewhere comfortable</li>
                  <li>• Remove distractions (phone on silent)</li>
                </ul>
              </div>
              
              <div className="bg-white border-2 border-purple-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-2">Music Suggestions</h3>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Instrumental/classical music</li>
                  <li>• Songs meaningful to your journey</li>
                  <li>• Nature sounds</li>
                  <li>• Meditation music</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Your Commitment Letter</h2>
            
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-6 mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Scroll className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-purple-800">Template to Inspire You</h3>
              </div>
              <div className="bg-white rounded-lg p-5 text-gray-700 space-y-4">
                <p className="italic">Dear [Your Name],</p>
                
                <p className="italic">Over the past 12 weeks, I have become someone who [describe the changes in yourself].</p>
                
                <p className="italic">I've learned that [key insights about yourself].</p>
                
                <p className="italic">I commit to continuing to [list your non-negotiable practices].</p>
                
                <p className="italic">When challenges arise, I will remember that [what you want to remember in hard moments].</p>
                
                <p className="italic">I am proud of myself for [what you accomplished].</p>
                
                <p className="italic">Going forward, I am a woman who [declare who you are now and who you're continuing to become].</p>
                
                <p className="italic">This is my promise to myself.</p>
                
                <p className="italic mt-4">With love,<br/>[Your Name]<br/>[Date]</p>
              </div>
            </div>

            <div className="bg-amber-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <Star className="w-5 h-5" />
                The Ritual
              </h2>
              <ol className="space-y-3 text-gray-700">
                <li><strong>1. Prepare:</strong> Set the scene, light your candle, start your music.</li>
                <li><strong>2. Breathe:</strong> Take 5 deep breaths to center yourself.</li>
                <li><strong>3. Reflect:</strong> Close your eyes and briefly recall your journey.</li>
                <li><strong>4. Read:</strong> Read your letter aloud to yourself with intention.</li>
                <li><strong>5. Sign:</strong> Sign and date your letter.</li>
                <li><strong>6. Seal:</strong> Close with another 3 deep breaths.</li>
                <li><strong>7. Keep:</strong> Put your letter somewhere you can find it when you need it.</li>
              </ol>
            </div>

            <div className="bg-rose-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-rose-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Congratulations
              </h2>
              <p className="text-gray-700 mb-4">
                You did it. 12 weeks of showing up for yourself. 12 weeks of learning, growing, 
                trying, and becoming. This isn't the end - it's the beginning of living as the 
                person you've worked so hard to become.
              </p>
              <p className="text-rose-700 font-medium">
                You are radiant. You are worthy. You are enough. Keep glowing.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
